import React, { useState } from 'react';
import { Calculator, Plus, Trash2, Save, FileText, DollarSign, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const EstimatingPage = () => {
  const [projectName, setProjectName] = useState('');
  const [clientName, setClientName] = useState('');
  const [lineItems, setLineItems] = useState([]);
  const [profitMargin, setProfitMargin] = useState(15);

  const addLineItem = (category) => {
    setLineItems([...lineItems, {
      id: Date.now(),
      category,
      description: '',
      quantity: 1,
      unitCost: 0,
      total: 0,
    }]);
  };

  const updateLineItem = (id, field, value) => {
    setLineItems(lineItems.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };
        if (field === 'quantity' || field === 'unitCost') {
          updated.total = updated.quantity * updated.unitCost;
        }
        return updated;
      }
      return item;
    }));
  };

  const removeLineItem = (id) => {
    setLineItems(lineItems.filter(item => item.id !== id));
  };

  const calculateTotals = () => {
    const laborTotal = lineItems.filter(i => i.category === 'labor').reduce((sum, i) => sum + i.total, 0);
    const materialsTotal = lineItems.filter(i => i.category === 'materials').reduce((sum, i) => sum + i.total, 0);
    const vendorTotal = lineItems.filter(i => i.category === 'vendor').reduce((sum, i) => sum + i.total, 0);
    const equipmentTotal = lineItems.filter(i => i.category === 'equipment').reduce((sum, i) => sum + i.total, 0);
    
    const subtotal = laborTotal + materialsTotal + vendorTotal + equipmentTotal;
    const profitAmount = subtotal * (profitMargin / 100);
    const grandTotal = subtotal + profitAmount;

    return { laborTotal, materialsTotal, vendorTotal, equipmentTotal, subtotal, profitAmount, grandTotal };
  };

  const handleSaveEstimate = () => {
    if (!projectName || !clientName) {
      toast.error('Please fill in project and client name');
      return;
    }
    toast.success('Estimate saved successfully!');
  };

  const handleExportPDF = () => {
    toast.success('Estimate exported to PDF!');
  };

  const totals = calculateTotals();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <img src="/williams-logo-transparent.png" alt="Williams Diversified" className="h-12 mb-2" />
          <h1 className="text-3xl font-bold" style={{ color: '#C9A961' }}>Project Estimating</h1>
          <p className="text-gray-400 mt-1">Create detailed cost estimates for construction projects</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={handleSaveEstimate}
            className="transition-all duration-200 hover:shadow-gold"
            style={{ backgroundColor: '#C9A961', color: '#000' }}
          >
            <Save className="mr-2 h-4 w-4" />
            Save Estimate
          </Button>
          <Button 
            variant="outline"
            onClick={handleExportPDF}
            style={{ borderColor: '#C9A961', color: '#C9A961' }}
          >
            <FileText className="mr-2 h-4 w-4" />
            Export PDF
          </Button>
        </div>
      </div>

      {/* Project Info */}
      <Card className="bg-dark-card border-dark-border shadow-card hover:shadow-card-hover transition-all duration-200">
        <CardHeader className="border-b" style={{ borderColor: '#C9A961' }}>
          <CardTitle style={{ color: '#C9A961' }}>Project Information</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label style={{ color: '#C9A961' }}>Project Name</Label>
              <Input
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="Enter project name"
                className="mt-1 bg-black border-dark-border text-white"
              />
            </div>
            <div>
              <Label style={{ color: '#C9A961' }}>Client Name</Label>
              <Input
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Enter client name"
                className="mt-1 bg-black border-dark-border text-white"
              />
            </div>
            <div className="md:col-span-2">
              <Label style={{ color: '#C9A961' }}>Project Description</Label>
              <Textarea
                placeholder="Brief project description"
                className="mt-1 bg-black border-dark-border text-white"
                rows={3}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cost Categories */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Button
          onClick={() => addLineItem('labor')}
          variant="outline"
          className="h-24 transition-all duration-200 hover:shadow-gold"
          style={{ borderColor: '#C9A961', color: '#C9A961' }}
        >
          <div className="text-center">
            <Plus className="h-6 w-6 mx-auto mb-2" />
            <p>Add Labor</p>
          </div>
        </Button>
        <Button
          onClick={() => addLineItem('materials')}
          variant="outline"
          className="h-24 transition-all duration-200 hover:shadow-gold"
          style={{ borderColor: '#C9A961', color: '#C9A961' }}
        >
          <div className="text-center">
            <Plus className="h-6 w-6 mx-auto mb-2" />
            <p>Add Materials</p>
          </div>
        </Button>
        <Button
          onClick={() => addLineItem('vendor')}
          variant="outline"
          className="h-24 transition-all duration-200 hover:shadow-gold"
          style={{ borderColor: '#C9A961', color: '#C9A961' }}
        >
          <div className="text-center">
            <Plus className="h-6 w-6 mx-auto mb-2" />
            <p>Add Vendor/Sub</p>
          </div>
        </Button>
        <Button
          onClick={() => addLineItem('equipment')}
          variant="outline"
          className="h-24 transition-all duration-200 hover:shadow-gold"
          style={{ borderColor: '#C9A961', color: '#C9A961' }}
        >
          <div className="text-center">
            <Plus className="h-6 w-6 mx-auto mb-2" />
            <p>Add Equipment</p>
          </div>
        </Button>
      </div>

      {/* Line Items */}
      {lineItems.length > 0 && (
        <Card className="bg-dark-card border-dark-border shadow-card">
          <CardHeader className="border-b" style={{ borderColor: '#C9A961' }}>
            <CardTitle style={{ color: '#C9A961' }}>Cost Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {lineItems.map((item) => (
                <div key={item.id} className="p-4 bg-black rounded-lg border border-dark-border">
                  <div className="flex items-start gap-4">
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div>
                        <Label className="text-xs" style={{ color: '#C9A961' }}>Category</Label>
                        <p className="text-sm text-white capitalize mt-1">{item.category}</p>
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-xs" style={{ color: '#C9A961' }}>Description</Label>
                        <Input
                          value={item.description}
                          onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                          placeholder="Item description"
                          className="mt-1 bg-gray-900 border-dark-border text-white text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs" style={{ color: '#C9A961' }}>Quantity</Label>
                        <Input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateLineItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                          className="mt-1 bg-gray-900 border-dark-border text-white text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs" style={{ color: '#C9A961' }}>Unit Cost ($)</Label>
                        <Input
                          type="number"
                          value={item.unitCost}
                          onChange={(e) => updateLineItem(item.id, 'unitCost', parseFloat(e.target.value) || 0)}
                          className="mt-1 bg-gray-900 border-dark-border text-white text-sm"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div>
                        <Label className="text-xs" style={{ color: '#C9A961' }}>Total</Label>
                        <p className="text-lg font-bold mt-1" style={{ color: '#C9A961' }}>
                          ${item.total.toLocaleString()}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLineItem(item.id)}
                        className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Totals Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Category Totals */}
        <Card className="lg:col-span-2 bg-dark-card border-dark-border shadow-card">
          <CardHeader className="border-b" style={{ borderColor: '#C9A961' }}>
            <CardTitle style={{ color: '#C9A961' }}>Cost Summary by Category</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-black rounded-lg">
                <span className="text-gray-400">Labor Costs</span>
                <span className="font-semibold text-white">${totals.laborTotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-black rounded-lg">
                <span className="text-gray-400">Materials</span>
                <span className="font-semibold text-white">${totals.materialsTotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-black rounded-lg">
                <span className="text-gray-400">Vendor/Subcontractor</span>
                <span className="font-semibold text-white">${totals.vendorTotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-black rounded-lg">
                <span className="text-gray-400">Equipment</span>
                <span className="font-semibold text-white">${totals.equipmentTotal.toLocaleString()}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Grand Total */}
        <Card className="bg-dark-card border-dark-border shadow-card">
          <CardHeader className="border-b" style={{ borderColor: '#C9A961' }}>
            <CardTitle style={{ color: '#C9A961' }}>Project Total</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Subtotal</span>
              <span className="font-semibold text-white">${totals.subtotal.toLocaleString()}</span>
            </div>
            <div>
              <Label style={{ color: '#C9A961' }}>Profit Margin (%)</Label>
              <Input
                type="number"
                value={profitMargin}
                onChange={(e) => setProfitMargin(parseFloat(e.target.value) || 0)}
                className="mt-1 bg-black border-dark-border text-white"
              />
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Profit Amount</span>
              <span className="font-semibold text-white">${totals.profitAmount.toLocaleString()}</span>
            </div>
            <div className="border-t pt-4" style={{ borderColor: '#C9A961' }}>
              <div className="flex justify-between items-center">
                <span className="text-xl font-bold" style={{ color: '#C9A961' }}>Grand Total</span>
                <span className="text-2xl font-bold" style={{ color: '#C9A961' }}>
                  ${totals.grandTotal.toLocaleString()}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EstimatingPage;